import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Code2,
  Key,
  Webhook,
  Plus,
  Copy,
  Trash2,
  Check,
  Eye,
  EyeOff,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const PERMISSIONS = [
  { id: "read", label: "Read", description: "Access posts, events, and public data" },
  { id: "write", label: "Write", description: "Create posts and content" },
  { id: "events", label: "Events", description: "Create and manage events" },
  { id: "ads", label: "Ads", description: "Manage advertising campaigns" },
];

const WEBHOOK_EVENTS = [
  { id: "post.created", label: "Post Created" },
  { id: "post.deleted", label: "Post Deleted" },
  { id: "event.created", label: "Event Created" },
  { id: "event.rsvp", label: "Event RSVP" },
  { id: "goal.completed", label: "Goal Completed" },
  { id: "user.registered", label: "User Registered" },
];

export default function Developer() {
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  const [createWebhookOpen, setCreateWebhookOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [newKeyForm, setNewKeyForm] = useState({
    name: "",
    permissions: ["read"],
  });
  const [newWebhookForm, setNewWebhookForm] = useState({
    url: "",
    events: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [keysRes, webhooksRes] = await Promise.all([
        axios.get(`${API}/developer/api-keys`),
        axios.get(`${API}/developer/webhooks`),
      ]);
      setApiKeys(keysRes.data);
      setWebhooks(webhooksRes.data);
    } catch (error) {
      toast.error("Failed to load developer data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/developer/api-keys`, newKeyForm);
      setNewApiKey(response.data.api_key);
      setApiKeys((prev) => [response.data, ...prev]);
      setNewKeyForm({ name: "", permissions: ["read"] });
      toast.success("API key created! Copy it now - you won't see it again.");
    } catch (error) {
      toast.error("Failed to create API key");
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    try {
      await axios.delete(`${API}/developer/api-keys/${keyId}`);
      setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
      toast.success("API key deleted");
    } catch (error) {
      toast.error("Failed to delete API key");
    }
  };

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/developer/webhooks`, newWebhookForm);
      setWebhooks((prev) => [response.data, ...prev]);
      setCreateWebhookOpen(false);
      setNewWebhookForm({ url: "", events: [] });
      toast.success("Webhook created!");
    } catch (error) {
      toast.error("Failed to create webhook");
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await axios.delete(`${API}/developer/webhooks/${webhookId}`);
      setWebhooks((prev) => prev.filter((w) => w.id !== webhookId));
      toast.success("Webhook deleted");
    } catch (error) {
      toast.error("Failed to delete webhook");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const togglePermission = (permission) => {
    setNewKeyForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const toggleWebhookEvent = (event) => {
    setNewWebhookForm((prev) => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter((e) => e !== event)
        : [...prev.events, event],
    }));
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 via-primary/10 to-secondary/10"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
              <Code2 className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Developer Portal
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Integrate HappyBook into your applications with our API
            </p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* API Documentation Link */}
        <div className="card-happy bg-gradient-to-r from-secondary/5 to-primary/5 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold mb-1">API Documentation</h2>
              <p className="text-muted-foreground text-sm">
                Learn how to integrate with HappyBook
              </p>
            </div>
            <Button variant="outline" className="rounded-full">
              <ExternalLink className="w-4 h-4 mr-2" />
              View Docs
            </Button>
          </div>
        </div>

        <Tabs defaultValue="api-keys" className="space-y-6">
          <TabsList>
            <TabsTrigger value="api-keys" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              API Keys
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="flex items-center gap-2">
              <Webhook className="w-4 h-4" />
              Webhooks
            </TabsTrigger>
          </TabsList>

          {/* API Keys Tab */}
          <TabsContent value="api-keys">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold">API Keys</h2>
                <p className="text-muted-foreground text-sm">
                  Manage your API keys for external integrations
                </p>
              </div>
              <Dialog open={createKeyOpen} onOpenChange={setCreateKeyOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-api-key-btn" className="btn-primary">
                    <Plus className="w-5 h-5 mr-2" />
                    Create API Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create API Key</DialogTitle>
                  </DialogHeader>
                  {newApiKey ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-sm text-green-800 mb-2 font-medium">
                          Your new API key (copy it now - you won't see it again):
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 p-2 bg-white rounded text-xs break-all">
                            {newApiKey}
                          </code>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => copyToClipboard(newApiKey)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setNewApiKey("");
                          setCreateKeyOpen(false);
                        }}
                        className="w-full btn-primary"
                      >
                        Done
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreateApiKey} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Key Name</Label>
                        <Input
                          data-testid="api-key-name-input"
                          placeholder="e.g., My App Integration"
                          value={newKeyForm.name}
                          onChange={(e) =>
                            setNewKeyForm((prev) => ({ ...prev, name: e.target.value }))
                          }
                          required
                          className="input-happy"
                        />
                      </div>
                      <div className="space-y-3">
                        <Label>Permissions</Label>
                        {PERMISSIONS.map((perm) => (
                          <div
                            key={perm.id}
                            className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-muted/50 cursor-pointer"
                            onClick={() => togglePermission(perm.id)}
                          >
                            <Checkbox
                              checked={newKeyForm.permissions.includes(perm.id)}
                              onCheckedChange={() => togglePermission(perm.id)}
                            />
                            <div>
                              <p className="font-medium">{perm.label}</p>
                              <p className="text-sm text-muted-foreground">
                                {perm.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button
                        type="submit"
                        data-testid="save-api-key-btn"
                        className="w-full btn-primary"
                      >
                        Create Key
                      </Button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="card-happy animate-pulse h-24"></div>
                ))}
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="card-happy text-center py-12">
                <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No API keys yet</h3>
                <p className="text-muted-foreground mb-6">
                  Create an API key to start integrating
                </p>
                <Button
                  onClick={() => setCreateKeyOpen(true)}
                  className="btn-primary"
                >
                  Create Your First Key
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="api-keys-list">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="card-happy flex items-center justify-between"
                    data-testid={`api-key-${key.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                        <Key className="w-6 h-6 text-secondary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{key.name}</h3>
                        <p className="text-sm text-muted-foreground font-mono">
                          {key.key_prefix}...
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {key.permissions.map((perm) => (
                            <Badge key={perm} variant="secondary" className="text-xs">
                              {perm}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm text-muted-foreground">
                        <p>Created {format(parseISO(key.created_at), "MMM d, yyyy")}</p>
                        {key.last_used && (
                          <p>Last used {format(parseISO(key.last_used), "MMM d")}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteApiKey(key.id)}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold">Webhooks</h2>
                <p className="text-muted-foreground text-sm">
                  Receive real-time notifications for events
                </p>
              </div>
              <Dialog open={createWebhookOpen} onOpenChange={setCreateWebhookOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-webhook-btn" className="btn-primary">
                    <Plus className="w-5 h-5 mr-2" />
                    Create Webhook
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Webhook</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateWebhook} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Endpoint URL</Label>
                      <Input
                        type="url"
                        data-testid="webhook-url-input"
                        placeholder="https://your-app.com/webhook"
                        value={newWebhookForm.url}
                        onChange={(e) =>
                          setNewWebhookForm((prev) => ({ ...prev, url: e.target.value }))
                        }
                        required
                        className="input-happy"
                      />
                    </div>
                    <div className="space-y-3">
                      <Label>Events to Subscribe</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {WEBHOOK_EVENTS.map((event) => (
                          <div
                            key={event.id}
                            className="flex items-center gap-2 p-3 rounded-xl border border-border hover:bg-muted/50 cursor-pointer"
                            onClick={() => toggleWebhookEvent(event.id)}
                          >
                            <Checkbox
                              checked={newWebhookForm.events.includes(event.id)}
                              onCheckedChange={() => toggleWebhookEvent(event.id)}
                            />
                            <span className="text-sm">{event.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button
                      type="submit"
                      data-testid="save-webhook-btn"
                      disabled={newWebhookForm.events.length === 0}
                      className="w-full btn-primary"
                    >
                      Create Webhook
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {webhooks.length === 0 ? (
              <div className="card-happy text-center py-12">
                <Webhook className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No webhooks yet</h3>
                <p className="text-muted-foreground mb-6">
                  Create a webhook to receive event notifications
                </p>
                <Button
                  onClick={() => setCreateWebhookOpen(true)}
                  className="btn-primary"
                >
                  Create Your First Webhook
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="webhooks-list">
                {webhooks.map((webhook) => (
                  <div
                    key={webhook.id}
                    className="card-happy"
                    data-testid={`webhook-${webhook.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Webhook className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-mono text-sm break-all">{webhook.url}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              variant={webhook.active ? "default" : "secondary"}
                              className={webhook.active ? "bg-green-500" : ""}
                            >
                              {webhook.active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {webhook.events.map((event) => (
                        <Badge key={event} variant="outline" className="text-xs">
                          {event}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground border-t border-border pt-3">
                      <span>Secret:</span>
                      <code className="bg-muted px-2 py-1 rounded text-xs">
                        {webhook.secret.slice(0, 12)}...
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(webhook.secret)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Navigation />
    </div>
  );
}
